import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
} from 'firebase/auth';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { KeyRound } from 'lucide-react';
import { auth } from '../lib/firebase';
import { useAuth } from '../auth/AuthProvider';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';

export function LoginPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation() as any;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // '/projects' không phải route nào cả — trước đây chỉ sống nhờ catch-all '*'.
  const from = location.state?.from?.pathname ?? '/apps';
  useEffect(() => {
    if (user) navigate(from, { replace: true });
  }, [user, from, navigate]);

  return (
    <div className="container flex flex-col items-center justify-center" style={{ minHeight: '80vh' }}>
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h1 style={{ 
          fontSize: '2.5rem', 
          fontWeight: 'bold', 
          background: 'linear-gradient(to right, #9b72ff, #ff72b6)',
          WebkitBackgroundClip: 'text',
          color: 'transparent',
          marginBottom: 0
        }}>
          MTSubKit
        </h1>
        <p className="text-muted" style={{ fontSize: '1.125rem', marginTop: '0.5rem' }}>Nền tảng Quản lý In-App Purchase SaaS</p>
      </div>

      <Card className="glass-panel" style={{ width: '100%', maxWidth: '450px', padding: '2rem', position: 'relative', overflow: 'hidden' }}>
        {/* Decorative background glow inside the card */}
        <div style={{
          position: 'absolute', top: 0, right: 0, width: '8rem', height: '8rem',
          backgroundColor: 'rgba(155, 114, 255, 0.4)', borderRadius: '50%',
          filter: 'blur(64px)', zIndex: -1
        }} />
        
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <KeyRound style={{ color: 'var(--primary)' }} />
          {mode === 'signin' ? 'Đăng nhập' : 'Tạo tài khoản'}
        </h2>
        
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', background: 'rgba(0,0,0,0.2)', padding: '0.25rem', borderRadius: '0.5rem', border: '1px solid rgba(255,255,255,0.1)' }}>
          <button
            style={{ flex: 1, padding: '0.5rem', borderRadius: '0.375rem', fontWeight: 500, transition: 'all 0.2s', background: mode === 'signin' ? 'rgba(255,255,255,0.1)' : 'transparent', color: mode === 'signin' ? 'white' : 'var(--text-muted)', border: 'none', cursor: 'pointer' }}
            onClick={() => setMode('signin')}
            disabled={busy}
          >
            Đăng nhập
          </button>
          <button
            style={{ flex: 1, padding: '0.5rem', borderRadius: '0.375rem', fontWeight: 500, transition: 'all 0.2s', background: mode === 'signup' ? 'rgba(255,255,255,0.1)' : 'transparent', color: mode === 'signup' ? 'white' : 'var(--text-muted)', border: 'none', cursor: 'pointer' }}
            onClick={() => setMode('signup')}
            disabled={busy}
          >
            Đăng ký
          </button>
        </div>

        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setError(null);
            setBusy(true);
            try {
              if (mode === 'signin') {
                await signInWithEmailAndPassword(auth, email, password);
              } else {
                await createUserWithEmailAndPassword(auth, email, password);
              }
              navigate(from, { replace: true });
            } catch (err: any) {
              setError(err?.message ?? 'Xác thực thất bại');
            } finally {
              setBusy(false);
            }
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
        >
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={busy}
            autoComplete="email"
            placeholder="dev@example.com"
          />
          <Input
            label="Mật khẩu"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={busy}
            autoComplete="current-password"
            placeholder="••••••••"
          />

          {error && <div style={{ padding: '0.75rem', background: 'rgba(255, 77, 79, 0.1)', border: '1px solid rgba(255, 77, 79, 0.3)', color: '#ff4d4f', borderRadius: '0.375rem', fontSize: '0.875rem' }}>{error}</div>}

          <Button type="submit" isLoading={busy} style={{ width: '100%', marginTop: '0.5rem' }} size="lg">
            {mode === 'signin' ? 'Đăng nhập' : 'Tạo tài khoản'}
          </Button>
        </form>

        <div style={{ position: 'relative', display: 'flex', padding: '1.25rem 0', alignItems: 'center' }}>
          <div style={{ flexGrow: 1, borderTop: '1px solid rgba(255,255,255,0.1)' }}></div>
          <span className="text-muted" style={{ margin: '0 1rem', fontSize: '0.875rem' }}>Hoặc</span>
          <div style={{ flexGrow: 1, borderTop: '1px solid rgba(255,255,255,0.1)' }}></div>
        </div>

        <Button
          variant="secondary"
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
          onClick={async () => {
            setError(null);
            setBusy(true);
            try {
              const provider = new GoogleAuthProvider();
              await signInWithPopup(auth, provider);
              navigate(from, { replace: true });
            } catch (err: any) {
              setError(err?.message ?? 'Đăng nhập Google thất bại');
            } finally {
              setBusy(false);
            }
          }}
          disabled={busy}
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          Tiếp tục với Google
        </Button>

        <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
          <a href="https://api-dxwsavkx7a-uc.a.run.app/docs" target="_blank" rel="noreferrer" style={{ color: 'var(--primary)', textDecoration: 'none', fontSize: '0.875rem' }}>
            📖 Xem Hướng Dẫn Tích Hợp (Dành cho Developer)
          </a>
        </div>
      </Card>
    </div>
  );
}
